import {
	Model,
	nil,
	PartialPropertyUpdateTree,
	Shape,
	Vector,
} from "@animech/axiom-format";
import { GridPlane } from "@animech/playcanvas-scripts";
import { isMobileDevice, safeResizeObserver } from "@animech/ui";
import debounce from "lodash.debounce";
import {
	Application,
	BoundingBox,
	CameraComponent,
	Color,
	Entity,
	GraphNode,
	Layer,
	Mat4,
	MeshInstance,
	ModelComponent,
	Plane,
	Ray,
	ScriptComponent,
	SHADER_PICK,
	SORTMODE_NONE,
	Vec2,
	Vec3,
} from "playcanvas";
import { clamp } from "@animech/camera";
import {
	arraysAreEqual,
	entityHasLayer,
	findPropertyReferences,
	isAncestorOf,
	isArrayProperty,
	isInSubtree,
	joinPaths,
	resolveProperty,
	vec3ToVector,
} from "@animech/axiom-utility";
import {
	WallTree,
	isWallRelatedObject,
	WallData,
	WallEditingTool,
	WallObjectBuilder,
} from "@animech/axiom-wall-builder";
import { assert } from "@animech/core-utilities";
import {
	ControllerManager,
	CursorStyle,
	SceneBuilder,
	SceneObjectInfo,
	SelectionStatus,
	ControllerEventType,
	isAncestorObjectOf,
	SceneRenderer,
	StaticSizeRenderer,
	ControllerRenderer,
	SceneRendererContext,
	SceneUpdateResult,
} from "@animech/axiom-scene-builder";
import {
	axiomViewToCameraView,
	getSubtreeGraphNodes,
	orbitCenter,
	viewSettings,
	SceneEngine,
	SceneEngineOptions,
} from "@animech/axiom-scene-utilities";
import { orthoHeightFromDistance } from "@animech/axiom-camera";
import {
	InvalidTemplatePropertyAccessError,
	PropertyState,
} from "@animech/axiom-configurator";
import interact from "interactjs";
import {
	AttacherDebugger,
	AttacherInstance,
	AttacherNode,
	AttacherSolver,
} from "@animech/supermodel";
import { saveAs } from "file-saver";
import { GLTFExporter } from "@animech/playcanvas-to-gltf";
import { ResolutionSwitcher } from "./resolution-switcher";
import { CameraSystem, Size } from "./camera-system";
import {
	controllerAssets,
	DragAndDropController,
	RotateController,
} from "./controllers";
import {
	DefinedAttacherAttributeMap,
	AttacherGroup,
	AttacherSolverContext,
	AttacherState,
	createAttacherSolverSettings,
	createEntityAttachmentSeparationMap,
	dynamicAttacherNodesByEntityName,
	entityNames,
	getAttachedEntities,
} from "./attachers";
import { CollisionSystem } from "./collision-system";
import {
	addEntityCollider,
	applyColliders,
	hideColliderMeshes,
	isSpecialModelParserNode,
} from "./colliders/utility";
import {
	dragAndDropSceneObjectOperationFactory,
	dropNewProductOperationFactory,
	DropSceneObjectOperationInterface,
} from "./drag-and-drop";
import { materialOverrides } from "./material-overrides";
import { Outline, ProgressiveAA } from "./scripts";
import { initSceneEngine, webgpuRequested } from "./graphics-device";
import { PickScheduler } from "./pick-scheduler";
import { GestureOwner } from "./captured-drag-gesture";
import { ScenePicker } from "./scene-picker";
import { PointerGestures } from "./pointer-gestures";
import {
	darkFloorForegroundColor,
	defaultFloorStyle,
	defaultWallColor,
	FloorStyleName,
	isDarkFloorStyle,
	Room,
	RoomAppearance,
	WallColorName,
} from "./room";
import { generatedRoomEnabled } from "./room/room-enabled";
import { createAttacherGroups } from "./attachers/attacher-groups";
import {
	BuildInfo,
	calculateBoundingBoxForEntity,
	Configurator,
	ConfiguratorChange,
	isProperty,
	logEntityBuilt,
	materialOverrideManager,
	castTo,
	findCommonAncestor,
	viewWithFocus,
	relativeQuadRotationMagnitude,
	viewsAreEqual,
	isMobile,
} from "src/utility";
import { ProductId, Root, root } from "src/definitions";
import {
	AnyWallEditingToolType,
	CameraView,
	CameraViewId,
	CameraViews,
	ConfigurationValue,
	ControllerType,
	GetConfigurationValue,
	ModelData,
	OnAction,
	Property,
	PropertyChangeMap,
	SceneObject,
	Transform,
	SceneTree,
	SceneStructure,
	PropertyDelta,
} from "src/common";
import { UiRenderer } from "src/ui";
import { MeasurementRenderer, Measurement } from "src/ui/measurement-renderer";
import { ElementDropZoneHandler } from "src/core/drag-and-drop";
import { AreaDescriptionManager } from "src/scene/area-description-manager";

type SceneManagerArgs = {
	configurator: Configurator;
	sceneDefinitions: SceneStructure;
	canvas: HTMLCanvasElement;
	assetBaseUrl: string;
	configPath: string;
	configurationMode: ConfigurationMode;
	cameraMode: CameraMode;
	activeProperty: Property;
	uiRenderer: UiRenderer;
	measurementRenderer: MeasurementRenderer;
	wallSnapSettings: WallSnapSettings;
	wallVisualizationSettings: WallVisualizationSettings;
	modelData: ModelData;
	onAction: OnAction;
	onSetActiveWallEditingTool: (
		type: AnyWallEditingToolType | undefined,
	) => void;
	onSetCameraMode: (mode: CameraMode, forceViewReset?: boolean) => void;
	onCameraIsTransitioningChanged: (transitioning: boolean) => void;
	onInstantiateProductTemplate: (
		productId: ProductId,
		disableHistory?: boolean,
	) => Property | undefined;
	onToggleDragOperationActive: (active: boolean) => void;
	autoRenderEnabled: boolean;
	autoCameraFocusEnabled: boolean;
};

export type SystemViews = {
	initial?: CameraView;
	target?: CameraView;
	cart?: CameraView;
};

export type CameraMode = "orbit" | "topDown";

export type ConfigurationMode = "product" | "room" | "walls";

export type WallSnapSettings = {
	snapToGrid: boolean;
	gridSpacing: number;
	nodeSnapDistance: number;
	nodeAxisSnapDistance: number;
};

export type WallVisualizationSettings = {
	fadeObscuringWalls: boolean;
};

export const config = {
	defaultResolution: 1,
	// A getter, so that importing this module does not read `window`.
	get stillResolution(): number {
		return Math.min(window.devicePixelRatio, 2);
	},
	upscaleDebounceTime: 300,
	resizeDebounceTime: 10,
	resizeEndDebounceTime: 50,
	sceneEngineOptions: ((): SceneEngineOptions => ({
		assetPrefix: "",
		scriptPrefix: "",
		contextOptions: {
			alpha: true,
			preserveDrawingBuffer: false,
		},
		scriptOrder: [],
	}))(),
	minimalRoomSize: {
		width: 8,
		height: 4,
		depth: 8,
	},
};

type DynamicWallStructure = {
	parent: SceneObject;
	wall: WallTree<Root>;
	wallsProperty: Property<WallData[]>;
};

function makeSelectionChanges(
	previousSelection: Entity[],
	newSelection: Entity[],
): Map<Entity, SelectionStatus> {
	const selected = newSelection.filter((e) => !previousSelection.includes(e));
	const deselected = previousSelection.filter((e) => !newSelection.includes(e));
	const selectionChanges = new Map<Entity, SelectionStatus>();
	for (const entity of selected) {
		selectionChanges.set(entity, "selected");
	}
	for (const entity of deselected) {
		selectionChanges.set(entity, "deselected");
	}
	return selectionChanges;
}

interface CollisionVisualizer {
	update(forceRefresh?: boolean): void;
}

export class SceneManager {
	private _configurator: Configurator;
	private _lastBuildState: PropertyState<Root, Root, ["root"]> | undefined;
	private _sceneDefinitions: SceneStructure;
	private _sceneProperties: Property[];
	private _resolution: number;
	private _assetBaseUrl: string;
	private _configPath: string;
	private _sceneEngine: SceneEngine;
	private _rootObject: SceneObject | undefined;
	private _sceneBuilder: SceneBuilder<Root>;
	private _sceneRenderer?: SceneRenderer<Root>;
	private _controllerRenderer: ControllerRenderer<Root, ControllerType>;
	private _uiRenderer: UiRenderer;
	private _measurementRenderer: MeasurementRenderer;
	private _wallObjectBuilder: WallObjectBuilder<Root>;
	private _controllerManager: ControllerManager<Root>;
	private _canvas: HTMLCanvasElement;
	private _activeScenePath?: string;
	private _cameraEntity?: Entity;
	private _cameraSystem?: CameraSystem;
	private _gizmoLayer?: Layer;
	private _outline?: Outline;
	private _progressiveAA?: ProgressiveAA;
	private _gridPlane?: GridPlane;
	private _dynamicRoom?: Room;
	private _wallColor: WallColorName = defaultWallColor;
	private _floorStyle: FloorStyleName = defaultFloorStyle;
	private _wallSnapSettings: WallSnapSettings;
	private _wallVisualizationSettings: WallVisualizationSettings;
	private _dynamicWallStructures: DynamicWallStructure[] = [];
	private _selectedEntities: Entity[] = [];
	private _sceneCreated = false;
	private _activeProperty: Property;
	private _cameraMode: CameraMode = "orbit";
	private _dragOperationActive = false;
	private _lastRequestedCameraView?: CameraView;
	private _pendingCameraUpdate?: () => void;
	private _debouncedCanvasResize = debounce(
		() =>
			this._updateRenderInfo(undefined, undefined, config.defaultResolution),
		config.resizeDebounceTime,
	);
	private _debouncedCanvasResizeEnd = debounce(
		() => this._updateRenderInfo(undefined, undefined, config.stillResolution),
		config.resizeEndDebounceTime,
	);
	private _canvasResizeObserver = safeResizeObserver(() => {
		this._debouncedCanvasResize(); // Render during interaction at half DPR for performance
		this._debouncedCanvasResizeEnd(); // Render final still frame at full DPR for sharpness
	});
	private _mode: ConfigurationMode;
	private _pickScheduler = new PickScheduler();
	private _scenePicker: ScenePicker;
	private _pointerGestures: PointerGestures<WallEditingTool<Root>>;

	private _onAction: OnAction;
	private _onSetActiveWallEditingTool: (
		type: AnyWallEditingToolType | undefined,
	) => void;
	private _onSetCameraMode: (
		mode: CameraMode,
		forceViewReset?: boolean,
	) => void;
	private _onToggleDragOperationActive: (active: boolean) => void;
	private _onCameraIsTransitioningChanged: (transitioning: boolean) => void;
	private _onInstantiateProductTemplate: (
		productId: ProductId,
		disableHistory?: boolean,
	) => Property | undefined;
	private _activeWallEditingTool?: WallEditingTool<Root>;
	private _modelData: ModelData;
	private _collisionSystem: CollisionSystem;
	private _attacherSolver?: AttacherSolver;
	private _attacherSolverContext?: AttacherSolverContext;
	private _attacherGroups: AttacherGroup[];
	private _attacherAttributeMap: DefinedAttacherAttributeMap;
	private _attacherState: AttacherState;
	private _attacherDebugger?: AttacherDebugger;
	private _attacherDebugMode = 0;
	private _canvasDropZoneManager: ElementDropZoneHandler;
	private _configurationBoundingBox?: BoundingBox;
	private _minimalRoomBoundingBox = new BoundingBox(
		new Vec3(),
		new Vec3(
			config.minimalRoomSize.width,
			config.minimalRoomSize.height,
			config.minimalRoomSize.depth,
		).divScalar(2),
	);
	private _roomBoundingBox?: BoundingBox;
	private _showConfigurationBoundingBox = false;
	private _showRoomBoundingBox = false;
	private _collisionVisualizer: CollisionVisualizer;
	private _areaDescriptionManager: AreaDescriptionManager;
	private _autoRenderEnabled: boolean;
	private _autoCameraFocusEnabled: boolean;

	constructor({
		configurator,
		sceneDefinitions,
		canvas,
		assetBaseUrl,
		configPath,
		configurationMode,
		cameraMode,
		activeProperty,
		uiRenderer,
		measurementRenderer,
		wallSnapSettings,
		wallVisualizationSettings,
		modelData,
		onAction,
		onSetActiveWallEditingTool,
		onSetCameraMode,
		onCameraIsTransitioningChanged,
		onInstantiateProductTemplate,
		onToggleDragOperationActive,
		autoRenderEnabled,
		autoCameraFocusEnabled,
	}: SceneManagerArgs) {
		this._configurator = configurator;
		this._sceneDefinitions = sceneDefinitions;
		this._sceneProperties = findPropertyReferences(sceneDefinitions.tree);
		this._canvas = canvas;
		this._canvas.dataset.sceneBuilding = "false";
		this._assetBaseUrl = assetBaseUrl;
		this._configPath = configPath;
		this._mode = configurationMode;
		this._cameraMode = cameraMode;
		this._resolution = config.defaultResolution;
		this._activeProperty = activeProperty;
		this._modelData = modelData;
		this._onAction = onAction;
		this._onSetActiveWallEditingTool = onSetActiveWallEditingTool;
		this._onSetCameraMode = onSetCameraMode;
		this._onCameraIsTransitioningChanged = onCameraIsTransitioningChanged;
		this._onInstantiateProductTemplate = onInstantiateProductTemplate;
		this._onToggleDragOperationActive = onToggleDragOperationActive;

		this._autoRenderEnabled = autoRenderEnabled;
		this._autoCameraFocusEnabled = autoCameraFocusEnabled;
		this._wallSnapSettings = { ...wallSnapSettings };
		this._wallVisualizationSettings = { ...wallVisualizationSettings };

		this._sceneEngine = new SceneEngine();
		this._areaDescriptionManager = new AreaDescriptionManager(
			this._sceneEngine,
		);

		this._controllerManager = new ControllerManager<Root>(
			{
				onAction: this._onAction,
				getConfigurationValue: this._getConfigurationValue,
				getEntity: (entity) => this._sceneBuilder.entityMap.get(entity),
				getCameraRay: this.getCameraRay,
				transformScreenToWorld: (x, y) =>
					this.findGridIntersection(this.getCameraRay(x, y)),
				pushAutoRenderRequest: () =>
					this._sceneEngine.autoRenderManager.pushAutoRenderRequest(),
				popAutoRenderRequest: () =>
					this._sceneEngine.autoRenderManager.popAutoRenderRequest(),
				setCursorStyle: this._setCursorStyle,
			},
			this._setCursorStyle,
		);
		this._sceneBuilder = new SceneBuilder(
			sceneDefinitions.tree as SceneTree,
			root,
			this._getConfigurationValue,
			{
				callbacks: {
					preprocessObject: this._preprocessSceneObject,
				},
			},
		);
		this._uiRenderer = uiRenderer;

		this._collisionSystem = new CollisionSystem();
		this._collisionVisualizer = this._createCollisionVisualizer();

		this._attacherAttributeMap = new DefinedAttacherAttributeMap(
			modelData.attacherNodesByModel,
			dynamicAttacherNodesByEntityName,
		);
		this._attacherState = new AttacherState();
		this._attacherGroups = createAttacherGroups(modelData.attacherNodesByModel);

		this._controllerRenderer = new ControllerRenderer<Root, ControllerType>(
			this._controllerManager,
		);
		this._controllerRenderer.registerFactory(
			"dragAndDrop",
			() =>
				new DragAndDropController(
					controllerAssets.iconMaterials.moveAllIcon,
					this._sceneBuilder,
					(object) =>
						dragAndDropSceneObjectOperationFactory(
							object,
							this._collisionSystem,
							this._attacherSolver!,
							this._createDropOperationInterface(),
						),
				),
		);
		this._controllerRenderer.registerFactory(
			"rotate",
			() => new RotateController(controllerAssets.iconMaterials.rotateIcon, {}),
		);

		this._wallObjectBuilder = new WallObjectBuilder({
			sceneBuilder: this._sceneBuilder,
			controllerRenderer: this._controllerRenderer,
			snapToGrid: (position) => this.snapToGrid(position),
			getCamera: () => this._cameraEntity!.camera!,
		});
		this._wallObjectBuilder.updateSnapSettings(this._wallSnapSettings);

		this._measurementRenderer = measurementRenderer;
		this._measurementRenderer.registerFactory((object) =>
			this._createMeasurementsForObject(object),
		);

		this._canvasDropZoneManager = new ElementDropZoneHandler(this._canvas, [
			(element) =>
				dropNewProductOperationFactory(
					element,
					this._collisionSystem,
					this._attacherSolver!,
					this._createDropOperationInterface(),
				),
		]);

		interact.pointerMoveTolerance(4);
		interact(canvas)
			.draggable({
				onstart: this._onCanvasInteraction,
				onmove: this._onCanvasInteraction,
				onend: this._onCanvasInteraction,
			})
			.on("tap", this._onCanvasTap)
			.on("move", this._onCanvasInteraction)
			.styleCursor(true);

		canvas.addEventListener("keyup", this._onCanvasKeyUp);
		this._scenePicker = new ScenePicker(
			canvas,
			this._pickScheduler,
			() => this._sceneEngine.Application,
			() => this._cameraEntity?.camera,
			() => this._invalidatePicking(),
		);
		this._pointerGestures = this._createPointerGestures();
	}

	private _resolutionSwitcher = new ResolutionSwitcher(
		() =>
			this._updateRenderInfo(undefined, undefined, config.defaultResolution),
		() => this._updateRenderInfo(undefined, undefined, config.stillResolution),
		config.upscaleDebounceTime,
	);
	private _onUpdate = () => {
		const autoRenderManager = this._sceneEngine.autoRenderManager;
		const isAutoRendering = autoRenderManager.hasAutoRenderRequest;

		this._resolutionSwitcher.update(isAutoRendering);

		if (autoRenderManager.hasSingleFrameRequest || isAutoRendering) {
			this._sceneRenderer?.update(this._makeRendererContext());
		}

		this._updateConfigurationBoundingBox();
		this._updateRoomBoundingBox();
	};

	get systemViews(): SystemViews {
		const { initialView, targetView, cartView } =
			this._sceneDefinitions.camera.settings;
		return {
			initial: initialView ? this.getCameraView(initialView) : undefined,
			target: targetView ? this.getCameraView(targetView) : undefined,
			cart: cartView ? this.getCameraView(cartView) : undefined,
		};
	}

	get wallSnapSettings(): WallSnapSettings {
		return { ...this._wallSnapSettings };
	}

	get wallVisualizationSettings(): WallVisualizationSettings {
		return { ...this._wallVisualizationSettings };
	}

	get activeWallEditingTool(): AnyWallEditingToolType | undefined {
		return this._activeWallEditingTool?.type;
	}

	get activeProperty(): Property {
		return this._activeProperty;
	}

	get gridTransform(): Mat4 {
		return this._gridPlane?.entity.getWorldTransform() ?? Mat4.IDENTITY;
	}

	get cameraMode(): CameraMode {
		return this._cameraMode;
	}

	get measurementRenderer(): MeasurementRenderer {
		return this._measurementRenderer;
	}

	get sceneBuilder(): SceneBuilder<Root> {
		return this._sceneBuilder;
	}

	get sceneEngine(): SceneEngine {
		return this._sceneEngine;
	}

	public exportToGLTF = async () => {
		const app = this._sceneEngine.Application;
		const root = this._sceneBuilder.rootEntity;

		const exporter = new GLTFExporter(app, {
			allowMultipleUVs: true,
			includeMeshInstance: (mi: MeshInstance) =>
				!mi.node.tags.has("excludeFromAR") &&
				!isSpecialModelParserNode(mi.node),
		});
		exporter.getOrAddScene(root);
		const blob = await exporter.toGLB();
		saveAs(blob, "my_design.glb");

		return Promise.resolve();
	};

	public async onSetCameraMode(
		mode: CameraMode,
		forceViewReset?: boolean,
	): Promise<void> {
		const changed = mode !== this._cameraMode;
		if (changed) {
			this._cameraMode = mode;
			this._wallObjectBuilder.setBuildMode(mode === "orbit" ? "3d" : "2d");
		}
		if (changed || forceViewReset) {
			await this._setDefaultView();
		}
	}

	public onActiveWallEditingToolChanged(
		type: AnyWallEditingToolType | undefined,
	): void {
		if (type !== this._activeWallEditingTool?.type) {
			this._pointerGestures.finish();
		}
		if (!type) {
			this._activeWallEditingTool?.cancel();
			this._activeWallEditingTool = undefined;
			return;
		}

		const cameraSystem = this._cameraSystem;
		assert(cameraSystem);
		assert(this._mode === "walls");

		const wallStructure = this._getActiveDynamicWallStructure();
		assert(wallStructure, "No editable walls found in scene definitions");

		if (type !== this._activeWallEditingTool?.type) {
			this._activeWallEditingTool?.cancel();
			const wallsRootEntity = this._getEntity(wallStructure.parent);
			const wallSettings = {
				height: this._getConfigurationValue(
					wallStructure.wall.settings.height,
					wallStructure.parent.archetype,
				),
				thickness: this._getConfigurationValue(
					wallStructure.wall.settings.thickness,
					wallStructure.parent.archetype,
				),
			};

			this._activeWallEditingTool = WallEditingTool.fromType(type, {
				app: this._sceneEngine.Application,
				builder: this._wallObjectBuilder,
				wallSettings,
				wallsRootEntity,
				autoRenderManager: this._sceneEngine.autoRenderManager,
				onFinished: () => this._onSetActiveWallEditingTool(undefined),
				interactionContext: {
					getCameraRay: (x, y) => this.getCameraRay(x, y),
					transformScreenToWorld: (x, y) =>
						this.findGridIntersection(this.getCameraRay(x, y)),
					getWallsProperty: () => {
						const property = this._getActiveWallsProperty();
						assert(property);
						return property;
					},
					onAction: this._onAction,
					findFreeChildPropertyIndices: (parent, count) =>
						this._findFreeChildPropertyIndices(parent, count),
				},
			});
		}
	}

	public render(): void {
		this._sceneEngine.render();
	}

	public onActivePropertyChanged(property: Property) {
		this._activeProperty = property;
		this._updateGrid();
		this._sceneEngine.render();
	}

	public onWallSnapSettingsChanged(settings: WallSnapSettings): void {
		this._wallSnapSettings = {
			...settings,
		};
		this._updateGrid();
		this._wallObjectBuilder.updateSnapSettings(this._wallSnapSettings);
		this._sceneEngine.render();
	}

	public onWallVisualizationSettingsChanged(
		settings: WallVisualizationSettings,
	): void {
		this._wallVisualizationSettings = {
			...settings,
		};
		this._wallObjectBuilder.fadeWallsThatObscureCamera(
			settings.fadeObscuringWalls,
		);
		this._sceneEngine.render();
	}

	public isSceneProperty(property: Property): boolean {
		return this._sceneProperties.some((p) => isInSubtree(property, p));
	}

	public renderToImageWithFocusView = async () => {
		assert(this._cameraSystem);

		const currentView = this._cameraSystem.view;
		const orbitCenter = this._cameraSystem.orbitCenter;
		this.select();

		// Reset camera focus using the cart view rotation for a deterministic
		// front-facing angle, regardless of the user's current viewport state.
		const savedLastView = this._lastRequestedCameraView;
		this._lastRequestedCameraView = this.getCameraView("cart");
		await this._resetCameraFocus(undefined, true, true, 0);
		this._lastRequestedCameraView = savedLastView;

		const imageUrl = this.renderToImage(800, 800);
		// Restore camera view to what was previously used before image was rendered.
		await this._cameraSystem.setView(currentView, orbitCenter, "instant", 0);
		this._sceneEngine.render();

		return imageUrl;
	};

	public renderToImage = (
		width: number,
		height: number,
		cameraView?: CameraView,
	): string => {
		let result = "";

		if (cameraView) {
			this.setCameraView(cameraView, 0);
		}

		this._updateRenderInfo(width, height, 1);
		this._measurementRenderer.render();

		const exportCanvas = document.createElement("canvas");
		const ctx = exportCanvas.getContext("2d");

		// include the measurements canvas in the image if possible
		if (ctx && this._measurementRenderer.canvas) {
			exportCanvas.width = this._canvas.width;
			exportCanvas.height = this._canvas.height;
			ctx.drawImage(this._canvas, 0, 0);
			ctx.drawImage(this._measurementRenderer.canvas, 0, 0);
			result = exportCanvas.toDataURL("image/webp");
		} else {
			result = this._canvas.toDataURL("image/webp");
		}

		this._updateRenderInfo();

		return result;
	};

	public async createScene(
		scenePath: string,
		initialCameraView?: CameraView,
	): Promise<void> {
		this._activeScenePath = scenePath;

		if (this._sceneCreated) {
			return await this.changeScene(scenePath, initialCameraView);
		}

		const sceneEngineOptions: SceneEngineOptions = Object.assign(
			{},
			config.sceneEngineOptions,
			{
				assetPrefix: joinPaths(
					this._assetBaseUrl,
					config.sceneEngineOptions.assetPrefix || "",
				),
				autoRenderEnabled: this._autoRenderEnabled,
			},
		);
		const configUrl: string = joinPaths(this._assetBaseUrl, this._configPath);

		await initSceneEngine(
			this._sceneEngine,
			webgpuRequested,
			this._canvas,
			configUrl,
			scenePath,
			sceneEngineOptions,
		);

		this._sceneEngine.Application.on("destroy", this._disposePicking);
		this._sceneEngine.Application.on("update", this._onUpdate);

		await this._wallObjectBuilder.init();

		Outline.register();
		GridPlane.register();

		const app = this._sceneEngine.Application;

		app.root.name = "RootNode";
		const modelTypes = ["model", "render", "container", "template"];

		await controllerAssets.initialize(app);

		const modelAssets = app.assets
			.filter(
				(a) =>
					modelTypes.includes(a.type) &&
					!a.name.toLowerCase().endsWith(".model"),
			)
			.sort((a, b) => a.name.localeCompare(b.name));

		this._sceneBuilder.assets = modelAssets;

		this._canvasResizeObserver.observe(this._canvas);

		this._sceneCreated = true;

		this._onSceneInitialized(app, initialCameraView);
		await this._dynamicRoom?.ready;

		this._collisionSystem.init(app, {
			colliderOverlapThreshold: 0.001, // Allows for 1mm of intersection
			onValidateEntityCollision: (entityA, entityB) => {
				const ancestor = castTo(
					findCommonAncestor(entityA, entityB, this.rootEntity),
					Entity,
				);

				// We assume that a common ancestor with a collider means
				// that both entities are part of the same product, and
				// collisions between these relatives should be ignored.
				const sharesProductAncestor =
					!!ancestor && this._collisionSystem.hasEntityBody(ancestor);

				return !sharesProductAncestor;
			},
		});

		this._attacherSolverContext = {
			attacherGroups: this._attacherGroups,
			attacherAttributeMap: this._attacherAttributeMap,
			snapRange: 0.5,
		};
		this._attacherSolver = new AttacherSolver({
			...createAttacherSolverSettings(this._attacherSolverContext),
			attacherNodesByEntityLookup: (entity: pc.Entity) => {
				let result: AttacherNode[] = [];

				const entityModelId = this._getEntityModelId(entity);

				if (entityModelId) {
					const modelNodes =
						this._modelData.attacherNodesByModel[entityModelId] ?? null;

					if (modelNodes) {
						result = modelNodes;
					}
				}

				const entityNodes = dynamicAttacherNodesByEntityName[entity.name];

				if (entityNodes) {
					result = [...result, ...entityNodes];
				}

				return result;
			},
		});

		app.on("prerender", () => {
			this._collisionVisualizer!.update();
			this._measurementRenderer.render();
		});

		if (BuildInfo.mode === "development") {
			this._initializeAttacherDebugger();
		}
	}

	public async changeScene(
		scenePath: string,
		initialCameraView?: CameraView,
	): Promise<void> {
		if (!this._sceneCreated) {
			throw new Error("Cannot change scene before creating scene");
		}

		const app = this._sceneEngine.Application;
		this._invalidatePicking();
		const oldScene = app.root.findByName("Root") as Entity;

		await new Promise<void>((resolve, reject) => {
			app.scenes.loadSceneSettings(scenePath, (error: unknown) => {
				if (error) {
					reject(error);
					return;
				}
				resolve();
			});
		});

		await new Promise<Entity | undefined>((resolve, reject) => {
			app.scenes.loadSceneHierarchy(
				scenePath,
				(error: unknown, newScene?: Entity) => {
					if (error) {
						reject(error);
						return;
					}
					resolve(newScene);
				},
			);
		});

		if (oldScene) {
			oldScene.destroy();
		}

		this._onSceneInitialized(app, initialCameraView);
		await this._dynamicRoom?.ready;
	}

	private _onSceneInitialized(
		app: Application,
		initialCameraView: CameraView | undefined,
	) {
		this._createGridPlane();
		this._createCamera(app, initialCameraView);
		this._createGizmoLayer();
		this._initializeRoom();

		const camera = this._cameraSystem?.cameraComponent;
		assert(camera, `Camera component is undefined after initializing scene`);
		this._sceneRenderer = new SceneRenderer<Root>(
			this._sceneBuilder,
			camera,
			this._getConfigurationValue,
		);
		this._sceneRenderer.registerRenderer(this._controllerRenderer);
		this._sceneRenderer.registerRenderer(new StaticSizeRenderer<Root>());
		this._sceneRenderer.registerRenderer(this._uiRenderer);
		this._sceneRenderer.registerRenderer(this._measurementRenderer);

		if (this._pendingCameraUpdate) {
			this._pendingCameraUpdate();
			this._pendingCameraUpdate = undefined;
		}
	}

	public setConfigurationMode(mode: ConfigurationMode) {
		if (this._mode === mode) {
			return;
		}
		this._pointerGestures.finish();
		this._mode = mode;
		this._cameraSystem?.setConfigurationMode(mode);
		this._onSetActiveWallEditingTool(undefined);
		const cameraMode = mode === "walls" ? "topDown" : "orbit";
		const resetView = cameraMode === "orbit" || cameraMode !== this._cameraMode;
		this._onSetCameraMode(cameraMode, resetView);

		// Rebuild product since the new mode may affect what gets built
		this.buildProduct();
	}

	public toggleDragOperationActive(active: boolean) {
		this._dragOperationActive = active;
	}

	public async pickEntities(
		x: number,
		y: number,
		width = 1,
		height = 1,
	): Promise<Entity[]> {
		const meshes = await this._pickMeshInstances(x, y, width, height);
		return meshes.map((mesh) => this._closestAncestorEntity(mesh.node));
	}

	public getCameraRay = (x: number, y: number): Ray => {
		if (!this._cameraEntity?.camera) {
			throw new Error(
				`No camera entity or camera component found when calculating camera ray`,
			);
		}

		const boundingRect = this._canvas.getBoundingClientRect();
		const xOffset = x - boundingRect.x;
		const yOffset = y - boundingRect.y;

		const camera = this._cameraEntity?.camera;
		assert(camera);
		const start = camera.screenToWorld(xOffset, yOffset, camera.nearClip);
		const end = camera.screenToWorld(xOffset, yOffset, camera.farClip);

		return new Ray(start, end.sub(start).normalize());
	};

	public getScreenPosition = (worldPosition: Vec3): Vec2 => {
		if (!this._cameraEntity?.camera) {
			throw new Error(
				`No camera entity or camera component found when calculating screen position`,
			);
		}

		const camera = this._cameraEntity?.camera;
		assert(camera);
		const boundingRect = this._canvas.getBoundingClientRect();
		const screenPosition = camera.worldToScreen(worldPosition);

		return new Vec2(
			screenPosition.x + boundingRect.x,
			screenPosition.y + boundingRect.y,
		);
	};

	public findGridIntersection(ray: Ray): Vec3 | undefined {
		assert(
			!!this._gridPlane,
			"No GridPlane component found when calculating grid intersection",
		);
		const { entity } = this._gridPlane;
		const gridY = entity.getPosition().y;
		const plane = new Plane(Vec3.UP, -gridY);
		const intersection = new Vec3();

		if (plane.intersectsRay(ray, intersection)) {
			intersection.y = gridY;
			return intersection;
		} else {
			return undefined;
		}
	}

	public snapToGrid(position: Vector): Vec3 {
		assert(
			!!this._gridPlane,
			"No GridPlane component found when calculating snapped grid position",
		);
		const {
			entity,
			minorGridLines: { spacing },
		} = this._gridPlane;
		const { x, z } = position;
		const gridPosition = entity.getPosition();
		return new Vec3(
			gridPosition.x + Math.round((x - gridPosition.x) / spacing) * spacing,
			gridPosition.y,
			gridPosition.z + Math.round((z - gridPosition.z) / spacing) * spacing,
		);
	}

	public select(...entities: Entity[]) {
		// A start still queued reads after this selection, so only earlier reads are stale.
		this._pointerGestures.finishEarlierReads();
		const previousSelection = [...this._selectedEntities];
		const selectableEntities = entities.flatMap((e) => {
			const selectableEntity = this._closestSelectableAncestorEntity(e);
			return selectableEntity ? [selectableEntity] : [];
		});
		const outline = this._outline;
		if (outline) {
			const entitiesToHideFromSelection = selectableEntities.flatMap((e) =>
				[...getSubtreeGraphNodes(e)].filter(
					(n): n is Entity =>
						n instanceof Entity &&
						(!n.enabled ||
							this._sceneBuilder.objectMap.get(n)?.hideFromSelection === true),
				),
			);
			this._sceneBuilder.asyncOperationsFinished().then(() => {
				outline.removeFromOutlineLayer(...previousSelection);
				outline.addToOutlineLayer(...selectableEntities);
				outline.removeFromOutlineLayer(...entitiesToHideFromSelection);
			});
		}
		this._selectedEntities = selectableEntities;
		if (this._selectedEntities.length === 0) {
			const clearSelectionAction =
				this._sceneDefinitions.events?.selectionClear;
			if (clearSelectionAction) {
				this._onAction({ action: clearSelectionAction, archetype: root });
			}
		} else {
			for (const entity of this._selectedEntities) {
				const object = this._sceneBuilder.objectMap.get(entity);
				if (object) {
					const action = object?.events?.select;
					if (action) {
						this._onAction({
							action,
							archetype: object.archetype,
						});
					}
				}
			}
		}
		this._sceneBuilder.updateScene(undefined);
		this._sceneRenderer?.onSelectionChanged(
			this._makeRendererContext(previousSelection),
		);

		const hasSelection = this._selectedEntities.length > 0;
		const selectionHasChanged = !arraysAreEqual(
			previousSelection,
			this._selectedEntities,
		);
		if (this._autoCameraFocusEnabled && hasSelection && selectionHasChanged) {
			this._resetCameraFocus(this._selectedEntities[0], false, false);
		}

		this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
	}

	private _makeRendererContext(
		previousSelection = this._selectedEntities,
	): SceneRendererContext<Root> {
		const selectedEntities = this._selectedEntities;
		const selectionChanges = makeSelectionChanges(
			previousSelection,
			selectedEntities,
		);
		return {
			selectionChanges,
			selectedEntities,
		};
	}

	public deleteSelected(): boolean {
		if (!this.canDeleteSelected()) {
			return false;
		}

		if (this._mode === "walls") {
			const selectedObjects = this._selectedEntities
				.map((entity) => this._sceneBuilder.objectMap.get(entity))
				.filter(isWallRelatedObject);
			for (const object of selectedObjects) {
				const parent = object.archetype?.getParent();
				if (parent && isArrayProperty(parent)) {
					this._configurator.set(object.archetype, nil);
				}
			}
			this.buildProduct();
			return true;
		} else if (this._mode === "room" || this._mode === "product") {
			const selectedObjects = this._selectedEntities
				.map((entity) => this._sceneBuilder.objectMap.get(entity))
				.filter(
					(object): object is SceneObject => object?.type === "sceneObject",
				);
			for (const object of selectedObjects) {
				const parent = object.archetype?.getParent();
				if (parent && isArrayProperty(parent)) {
					this._configurator.set(object.archetype, nil);
				}
			}
			this.buildProduct();
			return true;
		}
		return false;
	}

	public canDeleteSelected(): boolean {
		const slaveEntities = this._getAttachedEntities(
			this._selectedEntities,
			false,
			"slave",
		);

		return slaveEntities.length <= 0;
	}

	public get roomAppearance(): RoomAppearance {
		return { wallColor: this._wallColor, floorStyle: this._floorStyle };
	}

	/** Tints the generated room's wall. The choice survives scene changes. */
	public setWallColor(name: WallColorName): void {
		this._wallColor = name;
		if (this._dynamicRoom) {
			this._dynamicRoom.wallColor = name;
		}
	}

	/** Swaps the generated room's floor. The choice survives scene changes. */
	public setFloorStyle(name: FloorStyleName): void {
		this._floorStyle = name;
		if (this._dynamicRoom) {
			this._dynamicRoom.floorStyle = name;
		}
		this._updateGroundMeasurementColor();
	}

	private _updateGroundMeasurementColor(): void {
		const onDarkFloor =
			!!this._dynamicRoom && isDarkFloorStyle(this._floorStyle);
		this._measurementRenderer.groundLineColor = onDarkFloor
			? darkFloorForegroundColor
			: undefined;
	}

	public createRoom(width: number, depth: number): boolean {
		if (this._mode !== "walls" || width <= 0 || depth <= 0) {
			return false;
		}
		const wallsProperty = this._getActiveWallsProperty();
		if (!wallsProperty) {
			return false;
		}
		const halfWidth = width / 2;
		const halfDepth = depth / 2;
		const points = [
			new Vec3(-halfWidth, 0, -halfDepth),
			new Vec3(halfWidth, 0, -halfDepth),
			new Vec3(halfWidth, 0, halfDepth),
			new Vec3(-halfWidth, 0, halfDepth),
			new Vec3(-halfWidth, 0, -halfDepth),
		];
		this._addDynamicProperties(
			wallsProperty,
			...points.slice(0, 4).map((start, index) => {
				const end = points[index + 1];
				return {
					start: vec3ToVector(start),
					end: vec3ToVector(end),
					doors: [],
					windows: [],
				};
			}),
		);
		this.buildProduct();
		return true;
	}

	public addDoorToSelectedWall(): boolean {
		const entities = this._selectedEntities;
		if (this._mode !== "walls" || entities.length !== 1) {
			return false;
		}
		const wallsProperty = this._getActiveWallsProperty();
		const object = this._sceneBuilder.objectMap.get(entities[0]);
		if (!wallsProperty || !object) {
			return false;
		}
		const instance = this._wallObjectBuilder.objectInstances.get(object);
		if (!instance || !isAncestorOf(wallsProperty, object.archetype)) {
			return false;
		}
		const usedRects = instance.mergedOpeningRects;
		const freeX = Math.max(0, ...usedRects.map(({ right }) => right)) + 0.2;
		this._addDynamicProperties((object.archetype as Property<WallData>).doors, {
			dimensions: { x: 1, y: 2, z: 0 },
			offset: { x: freeX, y: 0, z: 0 },
		});
		this.buildProduct();
		return true;
	}

	public addWindowToSelectedWall(): boolean {
		const entities = this._selectedEntities;
		if (this._mode !== "walls" || entities.length !== 1) {
			return false;
		}
		const wallsProperty = this._getActiveWallsProperty();
		if (!wallsProperty) {
			return false;
		}
		const object = this._sceneBuilder.objectMap.get(entities[0]);
		if (!wallsProperty || !object) {
			return false;
		}
		const instance = this._wallObjectBuilder.objectInstances.get(object);
		if (!instance || !isAncestorOf(wallsProperty, object.archetype)) {
			return false;
		}
		const usedRects = instance.mergedOpeningRects;
		const freeX = Math.max(0, ...usedRects.map(({ right }) => right)) + 0.2;
		this._addDynamicProperties(
			(object.archetype as Property<WallData>).windows,
			{
				dimensions: { x: 0.8, y: 1, z: 0 },
				offset: { x: freeX, y: 0.8, z: 0 },
			},
		);
		this.buildProduct();
		return true;
	}

	public buildProduct(): void {
		if (!this._sceneCreated) {
			throw new Error("Cannot build product before creating scene");
		}
		this._canvas.dataset.sceneBuilding = "true";
		this._buildProduct();
		this._sceneBuilder.asyncOperationsFinished().then(() => {
			this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
			this._canvas.dataset.sceneBuilding = "false";
		});
		this._sceneEngine.render();
		this._updateGrid();
	}

	public getCameraView<TViewId extends CameraViewId>(
		viewId: TViewId,
	): CameraViews[TViewId] {
		return this._sceneDefinitions.camera.views[viewId];
	}

	public tryGetCameraView(viewId: string): CameraView | undefined {
		return this._sceneDefinitions.camera.views[viewId as CameraViewId];
	}

	public async setCameraView(view: CameraView, time: number): Promise<void> {
		this._lastRequestedCameraView = view;
		if (this._cameraMode === "topDown") {
			// The 2d view is currently a fixed top view which is not
			// allowed to be overridden.
			return;
		}
		return new Promise<void>((resolve) => {
			if (!this._sceneCreated || !this._cameraSystem) {
				this._pendingCameraUpdate = () => this.setCameraView(view, time);
				return;
			}

			const effectiveSettings = {
				...this._sceneDefinitions.camera.settings,
				...viewSettings(view),
			};

			const targetView = axiomViewToCameraView(view).setFov(
				effectiveSettings.fov,
			);

			this._cameraSystem?.setView(
				targetView,
				orbitCenter(view),
				time === 0 ? "instant" : "smooth",
				time,
			);

			this._cameraSystem?.updateSettings(effectiveSettings);

			resolve();
		});
	}

	public async setCameraViewFrom(
		startView: CameraView,
		endView: CameraView,
		time: number,
	): Promise<void> {
		this._lastRequestedCameraView = endView;
		if (this._cameraMode === "topDown") {
			// The 2d view is currently a fixed top view which is not
			// allowed to be overridden.
			return;
		}
		return new Promise<void>((resolve) => {
			if (!this._sceneCreated || !this._cameraSystem) {
				this._pendingCameraUpdate = () =>
					this.setCameraViewFrom(startView, endView, time);
				return;
			}
			this._cameraSystem?.setView(
				axiomViewToCameraView(startView),
				orbitCenter(startView),
				"instant",
			);
			this.setCameraView(endView, time);
			resolve();
		});
	}

	public resetCameraFocus(focusOnSelection: boolean, time?: number): void {
		this._resetCameraFocus(
			focusOnSelection ? this._selectedEntities.at(0) : undefined,
			true,
			true,
			time,
		);
	}

	public get canvas(): HTMLCanvasElement {
		return this._canvas;
	}

	public get activeScenePath(): string | undefined {
		return this._activeScenePath;
	}

	public get sceneCreated(): boolean {
		return this._sceneCreated;
	}

	public get rootEntity(): Entity | undefined {
		return this._sceneBuilder.rootEntity;
	}

	public refreshOverlays() {
		if (this._sceneCreated) {
			this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
		}
	}

	public renderAreaDescription(width: number, depth: number) {
		this._areaDescriptionManager.render(width, depth);
	}

	public get configurationBoundingBox(): BoundingBox | undefined {
		return this._configurationBoundingBox;
	}

	public get appRoot(): GraphNode | undefined {
		return this._sceneCreated ? this._sceneEngine.Application.root : undefined;
	}

	public get cameraEntity(): Entity | undefined {
		return this._cameraEntity;
	}

	private _createCamera(
		app: Application,
		initialCameraView: CameraView = {
			offset: 0,
			orbitPosition: { distance: 1, xRotation: 0, yRotation: 0 },
		} satisfies CameraView,
	) {
		const device = this._sceneEngine.Application.graphicsDevice;
		const deviceSize: Size = { width: device.width, height: device.height };
		let cameraEntity = (app.root.findByTag("Axiom Main Camera")[0] ??
			app.root.findByName("Camera")) as Entity | undefined;
		if (!cameraEntity) {
			cameraEntity = new Entity("Camera");
			cameraEntity.addComponent("camera") as CameraComponent;
			app.root.addChild(cameraEntity);
		}
		const cameraSystem = new CameraSystem(
			cameraEntity,
			this.canvas,
			deviceSize,
			axiomViewToCameraView(initialCameraView),
			orbitCenter(initialCameraView),
			this._sceneDefinitions.camera.settings,
			this._sceneEngine.autoRenderManager,
			this._onCameraIsTransitioningChanged,
		);
		const outlineColor = [0.192, 0.549, 0.906];
		const outline = cameraSystem.createOutline({
			color: [...outlineColor, 1],
			// interiorColor: [...outlineColor, 0.1],
			backgroundColor: [0.1, 0.1, 0.1, 0.4],
			useBackgroundColor: false,
			thickness: 2,
		});

		const progressiveAA = new ProgressiveAA(
			this._sceneEngine.Application,
			cameraEntity.findComponent("camera") as CameraComponent,
		);

		this._invalidatePicking();
		this._cameraEntity = cameraEntity;
		this._cameraSystem = cameraSystem;
		this._outline = outline;
		this._progressiveAA = progressiveAA;
	}

	private _createGridPlane() {
		const gridEntity = new Entity(entityNames.floor);
		const scriptComponent = gridEntity.addComponent(
			"script",
		) as ScriptComponent;
		this._sceneEngine.Application.root.addChild(gridEntity);
		this._gridPlane = scriptComponent.create(GridPlane, {
			attributes: {
				horizontalAxisLine: {
					enabled: true,
					thickness: 0.05,
				},
				verticalAxisLine: {
					enabled: true,
					thickness: 0.05,
				},
				majorGridLines: {
					enabled: true,
					color: [1, 1, 1, 1],
					spacing: 5,
					thickness: 0.08,
				},
				minorGridLines: {
					enabled: true,
					color: [1, 1, 1, 1],
					spacing: 1,
					thickness: 0.025,
				},
			},
		}) as GridPlane;
		this._updateGrid();

		// Disable picking for mesh instances that belong to the grid
		const modelComponents = this._gridPlane.entity.findComponents(
			"model",
		) as ModelComponent[];
		const meshInstances = modelComponents.flatMap(
			(model) => model.meshInstances ?? [],
		);
		for (const meshInstance of meshInstances) {
			meshInstance.pick = false;
		}
	}

	private _updateGrid() {
		const gridPlane = this._gridPlane;
		if (!gridPlane) {
			return;
		}
		const gridSettings = {
			enabled: false,
			spacing: 1,
			position: Vec3.ZERO,
		};

		if (this._mode === "walls") {
			const wallStructure = this._getActiveDynamicWallStructure();
			const parentEntity =
				wallStructure && this._sceneBuilder.entityMap.get(wallStructure.parent);

			gridSettings.enabled = true;
			gridSettings.spacing = this.wallSnapSettings.gridSpacing;
			gridSettings.position =
				parentEntity?.getWorldTransform().transformPoint(Vec3.ZERO) ??
				Vec3.ZERO;
		} else if (this._mode === "room") {
			// gridSettings.enabled = true;
		}

		gridPlane.entity.enabled = gridSettings.enabled;
		gridPlane.entity.setPosition(gridSettings.position);
		gridPlane.majorGridLines = {
			...gridPlane.majorGridLines,
			spacing: gridSettings.spacing * 5,
		};
		gridPlane.minorGridLines = {
			...gridPlane.minorGridLines,
			spacing: gridSettings.spacing,
		};
	}

	private _buildProduct() {
		const newBuildState = this._configurator.get(root).clone();
		const context = this._makeRendererContext();
		let delta: PropertyDelta | undefined;
		let sceneUpdateResult: SceneUpdateResult<Root>;

		if (!this._rootObject) {
			this._lastBuildState = newBuildState;
			try {
				sceneUpdateResult = this._sceneBuilder.buildScene();
				// TODO: Implement logic for applying colliders to all relevant entities using the _applyColliders method
				this._rootObject = this._sceneBuilder.rootObject;
				this._sceneRenderer?.onSceneUpdated(sceneUpdateResult, context);
				this._sceneEngine.Application.root.addChild(
					this._sceneBuilder.rootEntity,
				);
				if (BuildInfo.mode !== "production") {
					logEntityBuilt(this._sceneBuilder.rootEntity);
				}
			} catch (error) {
				if (error instanceof InvalidTemplatePropertyAccessError) {
					console.error(
						`Failed building product entity due to a template property access error – did you forget to add a "template" specification for a scene tree?`,
					);
				}
				throw error;
			}
		} else {
			if (this._lastBuildState) {
				delta = this._lastBuildState.getDelta(newBuildState);
				this._lastBuildState = newBuildState;
				if (this._rootObject) {
					const hadAutoRenderRequest =
						this._sceneEngine.autoRenderManager.hasAutoRenderRequest;
					if (hadAutoRenderRequest) {
						this._sceneEngine.setAutoRender(false);
					}
					sceneUpdateResult = this._sceneBuilder.updateScene(delta.changes);
					this._sceneRenderer?.onSceneUpdated(sceneUpdateResult, context);
					// Clear stale model components to prevent old model flash during async load
					for (const { entity } of sceneUpdateResult.modifiedObjects) {
						if (entity.model) {
							entity.removeComponent("model");
						}
					}
					if (sceneUpdateResult.createdObjects.length > 0) {
						const selectableObjects = sceneUpdateResult.createdObjects.filter(
							(o) => o.object.selectable,
						);
						if (selectableObjects.length > 0) {
							const selectableWithoutAncestors = selectableObjects.filter(
								(o1) =>
									!selectableObjects.some((o2) =>
										isAncestorObjectOf(o2.object, o1.object),
									),
							);
							if (selectableWithoutAncestors.length > 0) {
								this.select(selectableWithoutAncestors[0].entity);
							}
						}
					}
					if (sceneUpdateResult.deletedObjects.length > 0) {
						this._onObjectsDeleted(sceneUpdateResult.deletedObjects);
					}
					if (hadAutoRenderRequest) {
						this._sceneEngine.setAutoRender(true);
					}
					this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
				}
			}
		}
		this._resolveDynamicWalls(this._rootObject);

		this._sceneBuilder
			.asyncOperationsFinished()
			.then(() => this._postProcessBuiltProduct(sceneUpdateResult, delta));
	}

	private _postProcessBuiltProduct(
		updateResult: SceneUpdateResult<Root>,
		delta: PropertyDelta | undefined,
	) {
		const createdEntities = updateResult.createdObjects.map(
			(createdObject) => createdObject.entity,
		);
		const modifiedEntities = updateResult.modifiedObjects.map(
			(modifiedObject) => modifiedObject.entity,
		);
		const deletedEntities = updateResult.deletedObjects.map(
			(deletedObject) => deletedObject.entity,
		);

		// TODO: Consider caching the entities between calls
		const productEntities = this._getProductEntities();

		const attachmentSolution = this._solveAttachers(
			productEntities,
			createdEntities,
			modifiedEntities,
			deletedEntities,
		);
		const affectedEntities = attachmentSolution.entityTransforms.map(
			({ entity }) => entity,
		);
		const transformRelatedConfiguratorChanges =
			this._syncEntityTransformsToAxiom(affectedEntities);

		if (transformRelatedConfiguratorChanges.length > 0) {
			// These changes were done to ensure that transforms in
			// Axiom matches the current transforms of the corresponding
			// entities. Since everything is now in sync, we update
			// the last build state to include these changes.
			// They would otherwise be detected as "new" changes the next
			// time that buildProduct() is called, which is unnecessary.
			this._lastBuildState = this._configurator.get(root).clone();
		}

		this._applyColliders(productEntities, modifiedEntities, delta?.changes);

		const selectedEntity = this._selectedEntities.at(0);
		const isNewProductAdded =
			selectedEntity && modifiedEntities.includes(selectedEntity);
		if (this._autoCameraFocusEnabled && isNewProductAdded) {
			this._resetCameraFocus(undefined, true, false);
		}

		this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
	}

	private _resolveDynamicWalls(object: SceneObject) {
		if (object === this._rootObject) {
			this._dynamicWallStructures = [];
		}
		const tree = object.tree;

		for (const childTree of tree?.children ?? []) {
			if (childTree.type === "wall" && childTree.template) {
				this._dynamicWallStructures.push({
					parent: object,
					wall: childTree as WallTree<Root>,
					wallsProperty: resolveProperty(
						childTree.template,
						object.archetype,
					) as Property<WallData[]>,
				});
			}
		}

		for (const child of object.children ?? []) {
			this._resolveDynamicWalls(child);
		}
	}

	private _onObjectsDeleted(objects: SceneObjectInfo<Root>[]) {
		const selectedWithDeletedAncestor: Entity[] = [];
		for (const selectedEntity of this._selectedEntities) {
			if (
				objects.some(
					(info) =>
						info.entity === selectedEntity ||
						info.entity.isAncestorOf(selectedEntity),
				)
			) {
				selectedWithDeletedAncestor.push(selectedEntity);
			}
		}
		if (selectedWithDeletedAncestor.length > 0) {
			const newSelectedEntities = this._selectedEntities.filter(
				(e) => !selectedWithDeletedAncestor.includes(e),
			);
			this.select(...newSelectedEntities);
		}
		for (const info of objects) {
			const entity = info.entity;
			const parent = entity.parent;
			if (parent) {
				parent.removeChild(entity);
			}
			entity.destroy();
		}
	}

	private _solveAttachers(
		productEntities: Entity[],
		createdEntities: Entity[],
		_modifiedEntities: Entity[],
		_deletedEntities: Entity[],
	) {
		assert(this._attacherSolver);
		const environmentEntities = this._getEnvironmentEntitites();
		const frozenEntities: Entity[] = environmentEntities;

		for (const productEntity of productEntities) {
			if (createdEntities.includes(productEntity)) {
				continue;
			}

			frozenEntities.push(productEntity);
		}

		const solution = this._attacherSolver.findSolution(frozenEntities, [
			createdEntities,
		]);

		this._attacherSolver.applySolution(solution);
		this._attacherState.update(solution.instances);

		return solution;
	}

	private _getEntity(object: SceneObject): Entity {
		const entity = this._sceneBuilder.entityMap.get(object);
		if (!entity) {
			throw new Error(
				`Failed finding entity for scene object with name "${object.name}"`,
			);
		}
		return entity;
	}

	private _getAttachedEntities(
		entityGroup: Entity[],
		includePeers: boolean,
		rank: "master" | "slave",
		maxSeparation: number | null = null,
	): Entity[] {
		const { _attacherState: attacherState } = this;
		const environmentEntities = this._getEnvironmentEntitites();
		const environmentSeparationMap = createEntityAttachmentSeparationMap(
			attacherState,
			environmentEntities,
		);
		const attachmentFilter = (
			from: AttacherInstance,
			to: AttacherInstance,
		): boolean => {
			const fromEnvironmentSeparation = environmentSeparationMap.get(
				from.entity,
			);
			const toEnvironmentSeparation = environmentSeparationMap.get(to.entity);

			// Assuming for now that non-anchored entities can be ignored
			if (
				fromEnvironmentSeparation === undefined ||
				toEnvironmentSeparation === undefined
			) {
				return false;
			}

			const separationDelta =
				fromEnvironmentSeparation - toEnvironmentSeparation;

			if (separationDelta === 0 && includePeers) {
				return true;
			}

			if (maxSeparation !== null && Math.abs(separationDelta) > maxSeparation) {
				return false;
			}

			return rank === "master" ? separationDelta > 0 : separationDelta < 0;
		};

		return getAttachedEntities(entityGroup, attacherState, attachmentFilter);
	}

	private _getProductEntities(): Entity[] {
		const result: Entity[] = [];

		try {
			// eslint-disable-next-line unicorn/no-array-for-each
			this._sceneBuilder.rootEntity.forEach((graphNode) => {
				if (!(graphNode instanceof Entity)) {
					return;
				}

				const entityObject = this._sceneBuilder.objectMap.get(graphNode);

				if (entityObject?.type !== "sceneObject") {
					return;
				}

				result.push(graphNode);
			});
		} catch (e) {
			// The root entity might not be present and this is fine
		}

		return result;
	}

	private _getEnvironmentEntitites(): Entity[] {
		const result: Entity[] = [];

		const gridEntity = this._gridPlane?.entity;

		if (gridEntity) {
			result.push(gridEntity);
		}

		const dynamicRoomEntity = this._dynamicRoom?.entity;

		if (dynamicRoomEntity) {
			result.push(
				...(dynamicRoomEntity.find(
					(graphNode) => graphNode instanceof Entity,
				) as Entity[]),
			);
		}

		return result;
	}

	private _getEntityModelId(entity: Entity): string | null {
		const entityObject = this._sceneBuilder.objectMap.get(entity);
		const model = entityObject?.model;

		// TODO: Remove this utility function when it's been implemented in the platform.
		function isShape<T extends string>(
			model: Model<Root, T>,
		): model is Shape<Root> {
			return typeof model === "object" && "type" in model;
		}
		if (isShape(model)) {
			return null;
		}

		const resolvedModel =
			model && this._getConfigurationValue(model, entityObject.archetype);

		return typeof resolvedModel === "string" ? resolvedModel + ".glb" : null;
	}

	private _createGizmoLayer() {
		const camera = this._cameraEntity?.camera;
		if (!camera) {
			throw new Error("Cannot initialize a gizmo layer without a camera");
		}
		const gizmoLayer = new Layer({
			name: "Gizmo",
			clearDepthBuffer: true,
			opaqueSortMode: SORTMODE_NONE,
			transparentSortMode: SORTMODE_NONE,
		});

		const app = this._sceneEngine.Application;
		const layers = app.scene.layers;

		layers.push(gizmoLayer);
		camera.layers = camera.layers.concat(gizmoLayer.id);

		this._controllerManager.layer = gizmoLayer;

		this._gizmoLayer = gizmoLayer;

		const toggleLayer = () => {
			if (isMobile()) {
				gizmoLayer.enabled = false;
			} else {
				gizmoLayer.enabled = true;
			}
		};
		window.addEventListener("resize", toggleLayer);
		toggleLayer();
	}

	private _onCanvasTap = (ev: Interact.PointerEvent) => {
		if ("button" in ev.originalEvent && ev.originalEvent.button !== 0) {
			return;
		}
		const { clientX: x, clientY: y } = ev;
		this._scenePicker.ensureState();
		void this._pickScheduler
			.schedule(async (context) => {
				const meshes = await this._scenePicker.read(context, x, y);
				if (!context.isCurrent() || !meshes) {
					return;
				}
				const sceneRoot = this._sceneEngine.Application.root;
				const camera = this._cameraEntity?.camera;
				const apply = () => {
					this._scenePicker.ensureState();
					if (
						!context.isCurrent() ||
						sceneRoot !== this._sceneEngine.Application.root ||
						camera !== this._cameraEntity?.camera
					) {
						return;
					}
					const node = meshes[0]?.node;
					if (node) {
						let ancestor: GraphNode | null = node;
						while (ancestor && ancestor !== sceneRoot) {
							ancestor = ancestor.parent;
						}
						if (!ancestor) {
							return;
						}
					}
					const entity = node && this._closestAncestorEntity(node);
					if (
						entity &&
						this._gizmoLayer &&
						!entityHasLayer(entity, this._gizmoLayer)
					) {
						this.select(entity);
					} else if (!entity) {
						this.select();
					}
				};
				this._pointerGestures.applyTapEffect(apply);
			})
			.catch((error) => console.error("Selection readback failed", error));
	};

	private _onCanvasInteraction = (
		ev: Interact.DragEvent | Interact.PointerEvent<"move">,
	) => {
		const { type, clientX: x, clientY: y, shiftKey, interaction } = ev;
		if (type === "move" && interaction.pointerIsDown) {
			return;
		}
		// Always finish an accepted gesture, including when Shift changes mid-drag.
		if (shiftKey && type !== "dragend") {
			return;
		}
		if (
			!this._cameraEntity?.camera ||
			!this._cameraSystem ||
			!this._gizmoLayer
		) {
			return;
		}
		this._pointerGestures.handleInteraction(ev);
	};

	private _dispatchInteraction(
		type: string,
		x: number,
		y: number,
		picked?: MeshInstance,
	): { handled: boolean; startTool?: WallEditingTool<Root> } {
		const eventTypes: Record<string, ControllerEventType> = {
			move: "move",
			dragstart: "dragStart",
			dragmove: "dragMove",
			dragend: "dragEnd",
		};
		const handlerNames = {
			move: "onMove",
			dragstart: "onDragStart",
			dragmove: "onDrag",
			dragend: "onDragEnd",
		} as const;
		const handler = handlerNames[type as keyof typeof handlerNames];
		if (!handler) {
			return { handled: false };
		}
		let startTool: WallEditingTool<Root> | undefined;
		let requestedCursorStyle: CursorStyle | undefined;
		const requestCursorStyle = (style: string) => {
			if (!isMobileDevice()) {
				requestedCursorStyle = style as CursorStyle;
			}
		};
		let handled = this._controllerManager.handleEvent({
			type: eventTypes[type],
			mesh: picked,
			x,
			y,
		});
		if (!handled && this._mode === "walls" && this._activeWallEditingTool) {
			const tool = this._activeWallEditingTool;
			handled = tool[handler](x, y, picked, requestCursorStyle);
			if (handled && type === "dragstart") {
				startTool = tool;
			}
		}
		if (!handled && this._mode !== "product") {
			const selectableEntity =
				picked && this._closestSelectableAncestorEntity(picked.node);
			requestCursorStyle(selectableEntity ? "pointer" : "unset");
		}
		if (requestedCursorStyle !== undefined) {
			this._setCursorStyle(requestedCursorStyle);
		}
		return { handled, startTool };
	}
	private _captureStartTool(
		tool: WallEditingTool<Root> | undefined,
		invalidate: () => void,
	): GestureOwner | undefined {
		if (
			!tool ||
			tool.dragPicking !== "start-only" ||
			tool !== this._activeWallEditingTool ||
			tool.isFinished ||
			this._mode !== "walls"
		) {
			return;
		}
		const sceneRoot = this._sceneEngine.Application.root;
		const wallsProperty = this._getActiveWallsProperty();
		const wallStructure = this._getActiveDynamicWallStructure();
		const isCurrent = () => {
			const currentProperty = this._getActiveWallsProperty();
			return (
				this._mode === "walls" &&
				this._activeWallEditingTool === tool &&
				!!wallsProperty &&
				!!currentProperty &&
				!!wallStructure &&
				wallsProperty.equals(currentProperty) &&
				this._getActiveDynamicWallStructure()?.parent ===
					wallStructure.parent &&
				tool.isDragCurrent(
					sceneRoot,
					this._sceneBuilder.entityMap.get(wallStructure.parent),
				)
			);
		};
		if (!isCurrent()) {
			tool.cancel();
			invalidate();
			return;
		}
		const setCursor = (style: string) => {
			if (!isMobileDevice()) {
				this._setCursorStyle(style as CursorStyle);
			}
		};
		return {
			isCurrent,
			move: (x, y) => {
				tool.onDrag(x, y, undefined, setCursor);
				if (!tool.isFinished && !isCurrent()) {
					// Queue non-writing completion while the driver is delivering this movement.
					invalidate();
					return true;
				}
				return isCurrent();
			},
			finish: (x, y) => {
				tool.onDragEnd(x, y, undefined, setCursor);
			},
			invalidate: () => tool.cancel(),
		};
	}

	private _captureActiveDrag(): GestureOwner | undefined {
		return this._controllerManager.captureActiveDrag(
			this._sceneEngine.Application.root,
		);
	}
	private _createPointerGestures(): PointerGestures<WallEditingTool<Root>> {
		return new PointerGestures(
			this._canvas,
			this._pickScheduler,
			this._scenePicker,
			{
				dispatch: (type, x, y, picked) =>
					this._dispatchInteraction(type, x, y, picked),
				captureActiveDrag: () => this._captureActiveDrag(),
				capture: (tool, invalidate) => {
					const captured =
						this._captureActiveDrag() ??
						this._captureStartTool(tool, invalidate);
					if (!captured) {
						return;
					}
					const sceneRoot = this._sceneEngine.Application.root;
					const mode = this._mode;
					return {
						...captured,
						isCurrent: () =>
							sceneRoot === this._sceneEngine.Application.root &&
							mode === this._mode &&
							captured.isCurrent(),
					};
				},
				enableCameraInteractions: (enabled) =>
					this._cameraSystem?.enableInteractions(enabled),
			},
		);
	}

	private _onCanvasKeyUp = (ev: KeyboardEvent) => {
		if (this._activeWallEditingTool) {
			this._activeWallEditingTool.handleKeyUpEvent?.(ev);
		}

		const { _attacherDebugger: attacherDebugger } = this;

		if (ev.key === "F2") {
			if (attacherDebugger) {
				// Toggle attacher debugging
				this._attacherDebugMode = (this._attacherDebugMode + 1) % 4;

				this._attacherDebugger?.updateSettings({
					body: { enabled: this._attacherDebugMode >= 1 },
					axes: { enabled: this._attacherDebugMode === 2 },
					label: { enabled: this._attacherDebugMode === 3 },
				});
				this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
			}
		} else if (ev.key === "F4") {
			// Toggle display of colliders using F4, and use Shift+F4 to
			// toggle bounding boxes of colliding entities.
			if (ev.shiftKey) {
				this._collisionSystem.renderAabbsForCollidingEntities =
					!this._collisionSystem.renderAabbsForCollidingEntities;
				this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
			} else {
				this._collisionSystem.renderDebugColliders =
					!this._collisionSystem.renderDebugColliders;
				this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
			}
		} else if (ev.key === "F8") {
			const boundingBoxConfigs = {
				room: {
					renderBox: this._renderRoomBoundingBox,
					toggleVisibility: () =>
						(this._showRoomBoundingBox = !this._showRoomBoundingBox),
				},
				configuration: {
					renderBox: this._renderConfigurationBoundingBox,
					toggleVisibility: () =>
						(this._showConfigurationBoundingBox =
							!this._showConfigurationBoundingBox),
				},
			};
			const { renderBox, toggleVisibility } = ev.shiftKey
				? boundingBoxConfigs.room
				: boundingBoxConfigs.configuration;

			const visible = toggleVisibility();
			if (visible) {
				this._sceneEngine.Application.on("prerender", renderBox, this);
			} else {
				this._sceneEngine.Application.off("prerender", renderBox, this);
			}

			this._sceneEngine.autoRenderManager.pushSingleFrameRequest();
		} else if (ev.key === "f") {
			this._resetCameraFocus(this._selectedEntities.at(0));
		} else if (ev.key === "F") {
			this._resetCameraFocus();
		}
	};

	private _setCursorStyle = (style: CursorStyle) => {
		this._canvas.style.cursor = style;
	};

	private _addDynamicProperties = <T>(
		parent: Property<T[]>,
		...trees: PartialPropertyUpdateTree<Property<T>>[]
	) => {
		const childIndices = this._findFreeChildPropertyIndices(
			parent,
			trees.length,
		);
		return trees.map((tree, index) => {
			const childIndex = childIndices[index];
			this._configurator.set(parent[childIndex], tree);
			return parent[index];
		});
	};

	private _findFreeChildPropertyIndices<TValue>(
		parent: Property<TValue[]>,
		count: number,
	) {
		const { stateTree } = this._configurator.get(parent);
		let index = 0;
		const result: number[] = [];

		for (let i = 0; i < count; i++) {
			while (String(index) in stateTree) {
				++index;
			}
			result.push(index++);
		}
		return result;
	}

	private async _pickMeshInstances(
		x: number,
		y: number,
		width = 1,
		height = 1,
	): Promise<MeshInstance[]> {
		this._scenePicker.ensureState();
		return (
			(await this._pickScheduler.schedule((context) =>
				this._scenePicker.read(context, x, y, width, height),
			)) ?? []
		);
	}

	private _invalidatePicking(): void {
		this._pickScheduler.invalidate();
		try {
			this._pointerGestures.invalidate();
		} finally {
			this._scenePicker.invalidate();
		}
	}

	private _disposePicking = () => {
		try {
			this._invalidatePicking();
		} finally {
			this._pickScheduler.dispose();
			this._scenePicker.dispose();
			this._canvasResizeObserver.disconnect();
			this._debouncedCanvasResize.cancel();
			this._debouncedCanvasResizeEnd.cancel();
			this._canvas.removeEventListener("keyup", this._onCanvasKeyUp);
			this._pointerGestures.removeListeners();
			interact(this._canvas).unset();
		}
	};

	private _preprocessSceneObject = (_object: SceneObject) => {};

	private _getConfigurationValue: GetConfigurationValue = <T>(
		value: ConfigurationValue<T>,
		archetype: Property,
	): T => {
		if (!isProperty(value)) {
			return value;
		}
		const resolvedProperty = resolveProperty(value, archetype);
		return this._configurator.get(resolvedProperty).value as T;
	};

	private _updateRenderInfo(
		width?: number,
		height?: number,
		resolution?: number,
	) {
		this._invalidatePicking();
		if (this._sceneEngine) {
			this._sceneEngine.resizeCanvas(
				width,
				height,
				resolution || this._resolution,
			);
			this._sceneEngine.render();
		}
		if (width !== undefined && height !== undefined) {
			this._cameraSystem?.onDeviceResize({
				width: width ?? 0,
				height: height ?? 0,
			});
		}
	}

	private _closestAncestorEntity(node: GraphNode): Entity {
		if (node instanceof Entity) {
			return node;
		}
		if (node.parent === null) {
			throw new Error(
				`Failed finding ancestor entity, node has no parent: ${node}`,
			);
		}
		return this._closestAncestorEntity(node.parent);
	}

	private _closestSelectableAncestorEntity(
		node: GraphNode,
	): Entity | undefined {
		if (node instanceof Entity) {
			if (!node.parent) {
				return undefined;
			}
			const ignoredEntities = ["SphereBG", "shadow"];
			if (ignoredEntities.includes(node.name)) {
				return undefined;
			}
			const object = this._sceneBuilder.objectMap.get(node);
			if (object?.selectable) {
				return node;
			}
		}
		return node.parent !== null
			? this._closestSelectableAncestorEntity(node.parent)
			: undefined;
	}

	private _getActiveWallsProperty(): Property<WallData[]> | undefined {
		return this._mode === "walls" && isArrayProperty(this._activeProperty)
			? (this._activeProperty as Property<WallData[]>)
			: undefined;
	}

	private _getActiveDynamicWallStructure(): DynamicWallStructure | undefined {
		const wallsProperty = this._getActiveWallsProperty();
		return (
			wallsProperty &&
			this._dynamicWallStructures.find((wallStructure) =>
				wallStructure.wallsProperty.equals(wallsProperty),
			)
		);
	}

	private _entityPropertyLookup(entity: Entity, property: Property): unknown {
		const entityObject = this._sceneBuilder.objectMap.get(entity);

		if (!entityObject) {
			return null;
		}

		return this._getConfigurationValue(property, entityObject.archetype);
	}

	private _syncEntityTransformsToAxiom(entities: Entity[]) {
		const tempVec = new Vec3();
		const vectorDeltaTolerance = 1e-6;

		const configuratorChanges: ConfiguratorChange[] = [];

		type VectorTransform = Extract<Transform, { vector: unknown }>;

		const vectorTransformFilter =
			(type: VectorTransform["type"]) =>
			(x: Transform): x is VectorTransform =>
				x.type === type;

		const updateVectorProperty = (
			property: Property<Vector>,
			{ x, y, z }: Vector,
		) => {
			const previous = this._configurator.get(property).value;
			const delta = tempVec
				.set(x - previous.x, y - previous.y, z - previous.z)
				.length();

			if (delta > vectorDeltaTolerance) {
				const change = this._configurator.set(property, { x, y, z });
				configuratorChanges.push(change);
			}
		};

		for (const entity of entities) {
			const sceneObject = this._sceneBuilder.objectMap.get(entity);
			const transforms = sceneObject?.transforms;
			if (!transforms) {
				continue;
			}

			// TODO: What if there are multiple transforms of a given kind, such as "translate"?

			// `transform.vector` is `ConfigurationValue<Root, Vector>`
			// (= `Vector | Property<Vector>`). The bare `isProperty` predicate no
			// longer infers `TValue`, so narrow explicitly to `Property<Vector>`.
			const isVectorProperty = (
				value: ConfigurationValue<Vector>,
			): value is Property<Vector> => isProperty(value);

			const [translateProperty, rotateProperty, scaleProperty] = [
				vectorTransformFilter("translate"),
				vectorTransformFilter("rotate"),
				vectorTransformFilter("scale"),
			].map(
				(filter) =>
					transforms
						.filter(filter)
						.map((transform) => transform.vector)
						.filter(isVectorProperty)[0],
			);

			if (translateProperty) {
				updateVectorProperty(
					resolveProperty(translateProperty, sceneObject.archetype),
					entity.getLocalPosition(),
				);
			}

			if (rotateProperty) {
				updateVectorProperty(
					resolveProperty(rotateProperty, sceneObject.archetype),
					entity.getLocalEulerAngles(),
				);
			}

			if (scaleProperty) {
				updateVectorProperty(
					resolveProperty(scaleProperty, sceneObject.archetype),
					entity.getLocalScale(),
				);
			}
		}

		return configuratorChanges;
	}

	private async _setDefaultView() {
		const cameraSystem = this._cameraSystem;
		if (!cameraSystem) {
			return;
		}
		cameraSystem.updateSettings({
			...cameraSystem.settings,
			allowOrbit: this._cameraMode === "orbit",
		});
		const { view, orbitCenter } = cameraSystem;
		const transitionTime = 2;

		if (this._cameraMode === "topDown") {
			await cameraSystem.setView(
				view.setOrbit(20, 0, 90, orbitCenter).setProjection({
					type: "orthographic",
					height: orthoHeightFromDistance(20, view.fov),
				}),
				orbitCenter,
				"smooth",
				transitionTime,
			);
		} else {
			if (this._mode === "walls") {
				await cameraSystem.setView(
					view
						.setOrbit(20, 0, 45, orbitCenter)
						.setProjection({ type: "perspective" }),
					orbitCenter,
					"smooth",
					transitionTime,
				);
			} else if (this._mode === "product" && this._lastRequestedCameraView) {
				await this.setCameraView(this._lastRequestedCameraView, transitionTime);
			} else {
				await cameraSystem.setView(
					view
						.setOrbit(20, -90, 45, orbitCenter)
						.setProjection({ type: "perspective" }),
					orbitCenter,
					"smooth",
					transitionTime,
				);
			}
		}
	}

	private async _resetCameraFocus(
		targetEntity?: Entity,
		autoZoom = true,
		resetViewRotation = true,
		maxDuration = 1,
	) {
		const cameraSystem = this._cameraSystem;
		if (!cameraSystem) {
			return;
		}

		const target = targetEntity
			? this._calculateBoundingBoxForEntity(targetEntity, true)
			: this._configurationBoundingBox;
		if (!target) {
			return;
		}

		const axiomView = this._lastRequestedCameraView;
		const axiomCameraView = axiomView && axiomViewToCameraView(axiomView);
		const currentView = cameraSystem.view;
		const targetView = resetViewRotation
			? axiomCameraView ?? currentView
			: currentView;

		const activeTransitionTarget = cameraSystem.activeTransitionTarget;
		const orbitDistance =
			activeTransitionTarget?.orbitDistance ?? cameraSystem.orbitDistance;

		// On tablet/desktop the canvas extends behind UI panels, so we zoom out
		// to keep the product visible. On mobile the canvas matches the viewport.
		const canvasOversizeCompensation = isMobile() ? 1 : 2;
		const autoZoomMultiplier = autoZoom
			? canvasOversizeCompensation
			: undefined;

		const focusedTargetView = viewWithFocus(
			targetView,
			target,
			autoZoomMultiplier,
			cameraSystem.cameraComponent,
			orbitDistance,
		);

		if (
			activeTransitionTarget &&
			viewsAreEqual(focusedTargetView, activeTransitionTarget.view) &&
			Math.abs(orbitDistance - activeTransitionTarget.orbitDistance) <= 1e-6
		) {
			// No need to trigger a new transition to the same view
			return;
		}

		const relativeDistance = focusedTargetView.position.distance(
			currentView.position,
		);
		const relativeAngle = relativeQuadRotationMagnitude(
			currentView.rotation,
			focusedTargetView.rotation,
		);
		const idealDuration = Math.max(relativeDistance / 0.7, relativeAngle / 90);
		const duration = clamp(idealDuration, 0, maxDuration);

		await cameraSystem.setView(
			focusedTargetView,
			target.center,
			"smooth",
			duration,
		);
	}

	private _calculateBoundingBoxForEntity(
		entity: Entity,
		recursive: boolean,
		target?: BoundingBox,
	): BoundingBox | undefined {
		return calculateBoundingBoxForEntity(
			entity,
			recursive,
			(entity) => !entity.tags.has("excludeFromAR", "gizmo"),
			target,
		);
	}

	private _createMeasurementsForObject(
		_object: SceneObject,
	): Measurement[] | undefined {
		return undefined;
	}

	private async _applyColliders(
		productRootEntities: Entity[],
		modifiedEntities: Entity[],
		propertyChanges?: PropertyChangeMap,
	) {
		for (const productRootEntity of productRootEntities) {
			hideColliderMeshes(
				productRootEntity,
				this._outline ? [SHADER_PICK, this._outline.shaderPass] : [SHADER_PICK],
			);
			applyColliders(
				this._modelData.colliderNodesByModel,
				this._collisionSystem,
				productRootEntity,
				(entity) => this._getEntityModelId(entity),
				(entity) => modifiedEntities.includes(entity),
				(entity, property) => this._entityPropertyLookup(entity, property),
				propertyChanges,
			);
		}

		this._collisionSystem.step();
		this._collisionVisualizer.update(true); // Refresh is forced since mesh instances may have been replaced during (re-)build.
	}

	private _initializeAttacherDebugger() {
		const camera = this._cameraEntity?.camera;

		if (!camera) {
			throw "Failed to get camera component";
		}

		const { Application: application } = this._sceneEngine;
		const { canvas } = application.graphicsDevice;

		const debugCanvas = document.createElement("canvas");
		debugCanvas.setAttribute(
			"style",
			"position:absolute; pointer-events: none;",
		);
		debugCanvas.width = canvas.width;
		debugCanvas.height = canvas.height;

		canvas.parentElement?.insertBefore(debugCanvas, canvas);
		const context = debugCanvas.getContext("2d");

		if (!context) {
			throw "Failed to get 2d context";
		}

		this._attacherDebugger = new AttacherDebugger(context, camera, {
			axes: { enabled: false },
			body: { enabled: false },
			label: { enabled: false },
		});

		application.on("prerender", () => {
			this._attacherDebugger?.render(this._attacherState.getAll());
		});
	}

	private _createDropOperationInterface(): DropSceneObjectOperationInterface {
		return {
			getAttacherState: () => this._attacherState,
			getAttachedEntities: (entityGroup, includePeers, rank, maxSeparation) =>
				this._getAttachedEntities(
					entityGroup,
					includePeers,
					rank,
					maxSeparation,
				),
			updateAttacherInstances: (newAttacherInstances) => {
				this._attacherState.update(newAttacherInstances);
			},
			syncEntityTransformsToAxiom: (entities) =>
				this._syncEntityTransformsToAxiom(entities),

			getConfigurationValue: this._getConfigurationValue,

			getEntity: (entity) => this._sceneBuilder.entityMap.get(entity),

			getSceneObject: (object) => this._sceneBuilder.objectMap.get(object),

			getCameraRay: this.getCameraRay,
			getScreenPosition: this.getScreenPosition,
			transformScreenToWorld: (x, y) =>
				this.findGridIntersection(this.getCameraRay(x, y)),

			onAction: this._onAction,
			select: (...entities) => this.select(...entities),
			instantiateProduct: (productId, disableHistory) => {
				this._onInstantiateProductTemplate(productId, disableHistory);
				const [entity] = this._selectedEntities;
				const object = this._sceneBuilder.objectMap.get(entity);
				assert(object);
				return object;
			},
			toggleDragOperationActive: (active) => {
				this._onToggleDragOperationActive(active);
			},

			pushAutoRenderRequest: () =>
				this._sceneEngine.autoRenderManager.pushAutoRenderRequest(),
			popAutoRenderRequest: () =>
				this._sceneEngine.autoRenderManager.popAutoRenderRequest(),
			pushSingleFrameRequest: () =>
				this._sceneEngine.autoRenderManager.pushSingleFrameRequest(),
		};
	}

	private _initializeRoom() {
		const { Application: application } = this._sceneEngine;
		this._dynamicRoom?.destroy();
		this._dynamicRoom = undefined;

		const [entity] = application.root.findByTag("room");
		if (!generatedRoomEnabled || !(entity instanceof Entity)) {
			this._updateGroundMeasurementColor();
			return;
		}

		this._dynamicRoom = new Room(application, entity, {
			requestFrame: () =>
				this._sceneEngine.autoRenderManager.pushSingleFrameRequest(),
			wallColor: this._wallColor,
			floorStyle: this._floorStyle,
		});
		this._updateGroundMeasurementColor();

		entity.setPosition(0, 0, -this._minimalRoomBoundingBox.halfExtents.z);
		this._updateRoomBoundingBox();

		addEntityCollider(this._collisionSystem, entity, {
			type: "plane",
			normal: Vec3.UP,
		});
		addEntityCollider(this._collisionSystem, entity, {
			type: "plane",
			normal: Vec3.BACK,
		});

		this._collisionSystem.step();
	}

	private _updateConfigurationBoundingBox() {
		if (!this._lastBuildState || !this.rootEntity) {
			this._configurationBoundingBox = undefined;
			return;
		}
		this._configurationBoundingBox = this._calculateBoundingBoxForEntity(
			this.rootEntity,
			true,
			this._configurationBoundingBox,
		);
	}

	private _updateRoomBoundingBox() {
		const minimalBox = this._minimalRoomBoundingBox;
		const roomMin = minimalBox.getMin();
		const roomMax = minimalBox.getMax();

		const configurationBox = this._configurationBoundingBox;
		if (configurationBox) {
			const wallZ = roomMin.z;
			roomMin.min(configurationBox.getMin());
			roomMax.max(configurationBox.getMax());

			// Prevent room bounding box from passing behind the wall
			roomMin.z = Math.max(roomMin.z, wallZ);
		}

		this._roomBoundingBox ??= new BoundingBox();
		this._roomBoundingBox.setMinMax(roomMin, roomMax);

		if (this._dynamicRoom) {
			this._dynamicRoom.updateBoundsFromBoundingBox(this._roomBoundingBox);
		}
	}

	private _renderConfigurationBoundingBox() {
		if (!this._configurationBoundingBox) {
			return;
		}
		this._sceneEngine.Application.drawWireAlignedBox(
			this._configurationBoundingBox.getMin(),
			this._configurationBoundingBox.getMax(),
			Color.RED,
			false,
		);
	}

	private _renderRoomBoundingBox() {
		if (!this._roomBoundingBox) {
			return;
		}
		this._sceneEngine.Application.drawWireAlignedBox(
			this._roomBoundingBox.getMin(),
			this._roomBoundingBox.getMax(),
			Color.BLUE,
			false,
		);
	}

	private _createCollisionVisualizer(): CollisionVisualizer {
		const collisionSystem = this._collisionSystem;
		const added = new Set<Entity>();
		const removed = new Set<Entity>();
		let prevState = new Set<Entity>(collisionSystem.collidingEntities);

		const update = (forceRefresh = false) => {
			if (forceRefresh) {
				added.clear();
				removed.clear();
				prevState.clear();
			}

			const newState = collisionSystem.collidingEntities;

			// The room is what a product runs into, never what is in the way, so the
			// red stays on the product. Left in, its floor and wall would take the
			// tint too, since they are mesh instances on the room entity itself.
			const roomEntity = this._dynamicRoom?.entity;
			for (const newEntity of newState) {
				if (newEntity === roomEntity) {
					continue;
				}
				if (!prevState.has(newEntity)) {
					added.add(newEntity);
				}
			}
			for (const prevEntity of prevState) {
				if (!newState.has(prevEntity)) {
					removed.add(prevEntity);
				}
			}
			if (added.size > 0 || removed.size > 0) {
				materialOverrideManager.add(materialOverrides.collision, added);
				materialOverrideManager.remove(materialOverrides.collision, removed);
				added.clear();
				removed.clear();
				prevState = new Set(collisionSystem.collidingEntities);
			}
		};

		return {
			update,
		};
	}
}
